import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast, Toaster } from 'react-hot-toast';
import Pagination from '../components/Pagination';
import Swal from 'sweetalert2';

const colors = {
  1: { text: 'text-violet-300', bgSelected: 'bg-violet-500/10', borderSelected: 'border-violet-500', shadowSelected: 'shadow-violet-500/20' },
  2: { text: 'text-amber-300', bgSelected: 'bg-amber-500/10', borderSelected: 'border-amber-500', shadowSelected: 'shadow-amber-500/20' },
  3: { text: 'text-fuchsia-300', bgSelected: 'bg-fuchsia-500/10', borderSelected: 'border-fuchsia-500', shadowSelected: 'shadow-fuchsia-500/20' },
  4: { text: 'text-emerald-300', bgSelected: 'bg-emerald-500/10', borderSelected: 'border-emerald-500', shadowSelected: 'shadow-emerald-500/20' }
};

const TemplatesList = ({ isEmbedded = false, projectTemplateId = null, onTemplateCreated, initialView = 'list', onCancel }) => {
  const [view, setView] = useState(initialView); 
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  
  const [template, setTemplate] = useState({ id: null, name: '', description: '' });
  const [dbCategories, setDbCategories] = useState([]);
  const [showModal, setShowModal] = useState(null);

  // 4-Pane State
  const [selectedL1, setSelectedL1] = useState(null);
  const [selectedL2, setSelectedL2] = useState(null);
  const [selectedL3, setSelectedL3] = useState(null);

  // Tree Selection State
  const [selectedNodes, setSelectedNodes] = useState(new Set());

  // Quick Add Item Modal State
  const [newItemModal, setNewItemModal] = useState({ isOpen: false, name: '', parentId: null });

  useEffect(() => {
    if (view === 'list') fetchTemplates();
    fetchDbCategories();
  }, [view]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const url = isEmbedded
        ? `${process.env.REACT_APP_API_URL}/api/templates?include_custom=true`
        : `${process.env.REACT_APP_API_URL}/api/templates`;
      const { data } = await axios.get(url);
      setTemplates(data || []);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchDbCategories = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`);
      setDbCategories(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = async (id) => {
    try {
      const url = isEmbedded
        ? `${process.env.REACT_APP_API_URL}/api/templates?include_custom=true`
        : `${process.env.REACT_APP_API_URL}/api/templates`;
      const { data: tmplList } = await axios.get(url);
      const found = tmplList.find(t => t.id === parseInt(id));
      if (found) setTemplate(found);
      
      const { data: secList } = await axios.get(`${process.env.REACT_APP_API_URL}/api/templates/${id}/sections`);
      
      // Initialize selectedNodes
      const initSelected = new Set();
      secList.forEach(s => {
        if (s.sub_category_id) initSelected.add(Number(s.sub_category_id));
        let items = [];
        if (typeof s.items === 'string') items = JSON.parse(s.items);
        else if (Array.isArray(s.items)) items = s.items;
        items.forEach(itemId => initSelected.add(Number(itemId)));
      });
      setSelectedNodes(initSelected);
      
      // Reset pane selection or auto-expand first selected section
      if (secList.length > 0) {
        const firstSec = secList[0];
        if (firstSec.main_section_id) setSelectedL1(Number(firstSec.main_section_id));
        if (firstSec.category_id) setSelectedL2(Number(firstSec.category_id));
        if (firstSec.sub_category_id) setSelectedL3(Number(firstSec.sub_category_id));
      } else {
        setSelectedL1(null); setSelectedL2(null); setSelectedL3(null);
      }
      
      setView('form');
    } catch (e) {
      toast.error('Failed to load template details');
    }
  };

  const resetForm = () => {
    setTemplate({ id: null, name: '', description: '' });
    setSelectedNodes(new Set());
    setSelectedL1(null); setSelectedL2(null); setSelectedL3(null);
    setView('form');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!template.name.trim()) return toast.error('Template Name is required');
    setSaving(true);
    try {
      let savedTemplateId = template.id;
      if (template.id) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/templates/${template.id}`, template);
      } else {
        const { data: savedTemplate } = await axios.post(`${process.env.REACT_APP_API_URL}/api/templates`, template);
        savedTemplateId = savedTemplate.id;
      }
      
      // Compile payload from selectedNodes
      const selectedL3s = new Set();
      const selectedL4sByL3 = {};

      selectedNodes.forEach(id => {
        const cat = dbCategories.find(c => c.id === id);
        if (!cat) return;
        if (cat.level === 3) {
          selectedL3s.add(id);
        } else if (cat.level === 4) {
          if (!selectedL4sByL3[cat.parent_id]) selectedL4sByL3[cat.parent_id] = [];
          selectedL4sByL3[cat.parent_id].push(id);
          selectedL3s.add(cat.parent_id); 
        }
      });

      const payloadSections = Array.from(selectedL3s).map(l3Id => {
        const l3 = dbCategories.find(c => c.id === l3Id);
        const l2 = dbCategories.find(c => c.id === l3?.parent_id);
        const l1 = dbCategories.find(c => c.id === l2?.parent_id);
        return {
          main_section_id: l1?.id || null,
          category_id: l2?.id || null,
          sub_category_id: l3?.id || null,
          ref_id: l3?.id || null,
          items: selectedL4sByL3[l3Id] || []
        };
      });
      
      await axios.post(`${process.env.REACT_APP_API_URL}/api/templates/${savedTemplateId}/sections`, { sections: payloadSections });
      
      toast.success(template.id ? 'Template updated successfully!' : 'Template saved successfully!');
      if (onTemplateCreated) onTemplateCreated(savedTemplateId, template.name);
      setView('list');
    } catch (e) {
      toast.error('Error saving template');
    } finally {
      setSaving(false);
    }
  };

  const getDescendants = (nodeId, targetLevels) => {
    let result = [];
    const children = dbCategories.filter(c => c.parent_id === nodeId);
    children.forEach(c => {
      if (targetLevels.includes(c.level)) result.push(c.id);
      result = result.concat(getDescendants(c.id, targetLevels));
    });
    return result;
  };

  const isNodeFullySelected = (node) => {
    if (node.level === 4) return selectedNodes.has(node.id);
    if (node.level === 3) {
      const childrenL4 = dbCategories.filter(c => c.parent_id === node.id);
      if (childrenL4.length === 0) return selectedNodes.has(node.id);
      return childrenL4.every(c => selectedNodes.has(c.id));
    }
    const descendants = getDescendants(node.id, [3, 4]);
    if (descendants.length === 0) return false;
    return descendants.every(id => selectedNodes.has(id));
  };

  const isNodePartiallySelected = (node) => {
    if (node.level === 4) return false;
    if (node.level === 3) {
      const childrenL4 = dbCategories.filter(c => c.parent_id === node.id);
      if (childrenL4.length === 0) return false;
      const selectedCount = childrenL4.filter(c => selectedNodes.has(c.id)).length;
      return selectedCount > 0 && selectedCount < childrenL4.length;
    }
    const descendants = getDescendants(node.id, [3, 4]);
    if (descendants.length === 0) return false;
    const selectedCount = descendants.filter(id => selectedNodes.has(id)).length;
    return selectedCount > 0 && selectedCount < descendants.length;
  };

  const handleToggleNode = (node) => {
    const newSelected = new Set(selectedNodes);
    const fullySelected = isNodeFullySelected(node);

    if (node.level === 4) {
      if (fullySelected) newSelected.delete(node.id);
      else newSelected.add(node.id);
    } 
    else if (node.level === 3) {
      const childrenL4 = dbCategories.filter(c => c.parent_id === node.id);
      if (fullySelected) {
        newSelected.delete(node.id);
        childrenL4.forEach(c => newSelected.delete(c.id));
      } else {
        newSelected.add(node.id);
        childrenL4.forEach(c => newSelected.add(c.id));
      }
    }
    else {
      const descendants = getDescendants(node.id, [3, 4]);
      if (fullySelected) {
        descendants.forEach(id => newSelected.delete(id));
      } else {
        descendants.forEach(id => newSelected.add(id));
      }
    }
    setSelectedNodes(newSelected);
  };

  const handleQuickCreateItem = async (name, parentId) => {
    try {
      const payload = {
        name: name.trim(),
        level: 4,
        parent_id: Number(parentId),
        sort_order: 0
      };
      const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/api/categories`, payload);
      toast.success(`Item "${name}" created`);
      
      const newCatId = data?.id || data?.insertId;
      if (newCatId) {
        setSelectedNodes(prev => new Set([...prev, newCatId]));
      }
      fetchDbCategories();
    } catch (e) {
      toast.error('Failed to create item');
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/templates/${deleteModal.id}`);
      toast.success('Template deleted');
      setDeleteModal(null);
      fetchTemplates();
    } catch (err) {
      toast.error('Error deleting template');
    }
  };

  const handleShow = async (t) => {
    try {
      const { data: secList } = await axios.get(`${process.env.REACT_APP_API_URL}/api/templates/${t.id}/sections`);
      setShowModal({
        template: t,
        sections: secList.length > 0 ? secList.map(s => ({
          main_section_id: s.main_section_id || '',
          category_id: s.category_id || '',
          sub_category_id: s.sub_category_id || '',
          ref_id: s.ref_id || '',
          items: (s.items && typeof s.items === 'string') ? JSON.parse(s.items) : (s.items || [])
        })) : []
      });
    } catch (e) {
      toast.error('Failed to load template details');
    }
  };

  const filteredTemplates = templates.filter(t => {
    const matchSearch = (t.name && t.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (t.description && t.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchProject = !isEmbedded || !projectTemplateId || String(projectTemplateId).split(',').map(id => Number(id.trim())).includes(t.id);
    return matchSearch && matchProject;
  });

  const totalPages = Math.ceil(filteredTemplates.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredTemplates.slice(indexOfFirstRecord, indexOfLastRecord);

  const l1Items = dbCategories.filter(c => c.level === 1).sort((a,b) => a.sort_order - b.sort_order);
  const l2Items = selectedL1 ? dbCategories.filter(c => c.level === 2 && c.parent_id === selectedL1).sort((a,b) => a.sort_order - b.sort_order) : [];
  const l3Items = selectedL2 ? dbCategories.filter(c => c.level === 3 && c.parent_id === selectedL2).sort((a,b) => a.sort_order - b.sort_order) : [];
  const l4Items = selectedL3 ? dbCategories.filter(c => c.level === 4 && c.parent_id === selectedL3).sort((a,b) => a.sort_order - b.sort_order) : [];

  const renderPane = (level, title, items, selectedId, onSelect, parentId) => {
    const c = colors[level];
    
    return (
      <div className="w-[320px] flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner shrink-0 h-fit max-h-[calc(100vh-200px)] min-h-[450px]">
        <div className="p-4 bg-slate-800/80 border-b border-slate-700 flex justify-between items-center">
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider">{title}</h3>
            <p className="text-xs text-slate-400 mt-1">{items.length} items</p>
          </div>
          {level === 4 && items.length > 0 && (
            <button 
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const allSelected = items.every(i => selectedNodes.has(i.id));
                const newSelected = new Set(selectedNodes);
                if (allSelected) {
                  items.forEach(i => newSelected.delete(i.id));
                } else {
                  items.forEach(i => newSelected.add(i.id));
                }
                setSelectedNodes(newSelected);
              }}
              className="text-[10px] font-bold px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30 transition uppercase tracking-wider"
            >
              {items.every(i => selectedNodes.has(i.id)) ? 'Deselect All' : 'Select All'}
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-3 custom-scrollbar space-y-2">
          {items.length === 0 ? (
             <div className="text-center py-10 text-slate-500 text-xs italic">No items available</div>
          ) : items.map(item => {
            const isSelected = selectedId === item.id;
            const isFullyChecked = isNodeFullySelected(item);
            const isPartiallyChecked = isNodePartiallySelected(item);
            
            return (
              <div 
                key={item.id} 
                onClick={() => onSelect && onSelect(item.id)} 
                className={`cursor-pointer p-3.5 rounded-xl border transition-all select-none group flex items-center justify-between gap-2 shadow-sm ${isSelected ? `${c.bgSelected} ${c.borderSelected} ${c.shadowSelected}` : 'bg-slate-800 border-slate-700 hover:border-slate-500 hover:bg-slate-700/80'}`}
              >
                <div className="flex items-center gap-3 overflow-hidden flex-1">
                  <h4 className={`font-bold text-sm truncate ${isSelected ? c.text : 'text-slate-200'}`}>{item.name}</h4>
                </div>
                
                <div 
                  onClick={(e) => { e.stopPropagation(); handleToggleNode(item); }}
                  className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                    isFullyChecked 
                      ? 'bg-emerald-500 border-emerald-500 text-slate-900 shadow-[0_0_10px_rgba(16,185,129,0.3)]' 
                      : isPartiallyChecked 
                        ? 'bg-emerald-500/30 border-emerald-500 text-emerald-400' 
                        : 'bg-slate-950 border-slate-600 hover:border-emerald-500'
                  }`}
                >
                  {isFullyChecked && (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                  )}
                  {isPartiallyChecked && !isFullyChecked && (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4"/></svg>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {level === 4 && selectedL3 && (
          <div className="p-3 bg-slate-800/50 border-t border-slate-700">
            <button 
              type="button"
              onClick={() => {
                setNewItemModal({ isOpen: true, name: '', parentId });
              }}
              className="w-full text-emerald-400 text-xs font-bold py-3 rounded-xl border border-emerald-500/30 hover:bg-emerald-500/10 transition flex items-center justify-center gap-2 shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
              Quick Add Item
            </button>
          </div>
        )}
      </div>
    );
  };

  const innerView = (
    <>
      <style>{`.custom-scrollbar::-webkit-scrollbar { height: 8px; width: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; } .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #475569; }`}</style>
      {view === 'list' ? (
        <div className="bg-slate-900/20 border border-slate-900 rounded-3xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-white text-lg">Templates Directory</h3>
            <div className="flex gap-3">
              <input 
                type="text" 
                placeholder="Search templates..." 
                className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm outline-none focus:border-emerald-500"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
              {!isEmbedded && (
                <button
                  onClick={resetForm}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold px-4 py-2 rounded-xl text-sm transition shadow-lg shadow-emerald-500/20"
                >
                  Add Template
                </button>
              )}
            </div>
          </div>

          <div className="w-full">
            {loading ? (
              <div className="text-slate-400 text-sm text-center py-8">Loading templates...</div>
            ) : templates.length === 0 ? (
              <div className="text-slate-500 text-sm italic text-center py-8">No templates found. Create one!</div>
            ) : (
              <table className="w-full text-left border-collapse text-sm text-gray-300">
                <thead>
                  <tr className="border-b border-slate-800 text-gray-500 font-mono whitespace-nowrap">
                    <th className="pb-3 pr-4">S.No</th>
                    <th className="pb-3 pr-4">Template Name</th>
                    <th className="pb-3 pr-4">Description</th>
                    <th className="pb-3 pr-4 text-center">Template Status</th>
                    <th className="pb-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {currentRecords.map((t, index) => (
                    <tr key={t.id} className="hover:bg-slate-800/20 transition-colors">
                      <td className="py-4 text-gray-400 pr-4">{indexOfFirstRecord + index + 1}</td>
                      <td className="py-4 font-semibold text-emerald-300 pr-4">{t.name}</td>
                      <td className="py-4 pr-4">{t.description || '-'}</td>
                      <td className="py-4 pr-4 text-center">
                        <span className="px-2 py-1 rounded-md text-xs bg-emerald-500/10 text-emerald-400">
                          Active
                        </span>
                      </td>
                      <td className="py-4 text-right min-w-[160px]">
                        <div className="flex justify-end gap-3 items-center">
                          <button onClick={() => handleShow(t)} className="text-emerald-400 hover:text-emerald-300 p-1.5 hover:bg-emerald-500/10 rounded transition" title="Show Template Details">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          </button>
                          <button onClick={() => handleEdit(t.id)} className="text-blue-400 hover:text-blue-300 p-1.5 hover:bg-blue-500/10 rounded transition" title="Edit Template">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                          <button onClick={() => setDeleteModal({ id: t.id })} className="text-rose-500 hover:text-rose-400 p-1.5 hover:bg-rose-500/10 rounded transition" title="Remove Template">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!loading && templates.length > 0 && (
              <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} totalItems={filteredTemplates.length} />
            )}
          </div>
        </div>
      ) : (
        <div className={isEmbedded ? "w-full pt-4" : "bg-slate-900/60 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-xl overflow-hidden w-full mx-auto"}>
          <form onSubmit={handleSave} className="flex flex-col">
            <div className={`p-6 border-b border-slate-700 flex justify-between items-center ${isEmbedded ? 'pr-12' : ''}`}>
              <div>
                <h2 className="text-xl font-bold text-white">{template.id ? 'Edit Template' : 'Create New Template'}</h2>
                <p className="text-xs text-slate-400 mt-1">Select categories to include in this template using the checkboxes</p>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Template Name *</label>
                  <input 
                    type="text" 
                    value={template.name}
                    onChange={e => setTemplate({...template, name: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 p-3 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition"
                    placeholder="e.g. Standard Financial Report"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Description</label>
                  <input 
                    type="text" 
                    value={template.description}
                    onChange={e => setTemplate({...template, description: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 p-3 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition"
                    placeholder="Optional description"
                  />
                </div>
              </div>

              <div className="flex gap-4 overflow-x-auto pb-4 w-full custom-scrollbar items-start">
                {renderPane(1, 'LEVEL 1 (MAIN SECTION)', l1Items, selectedL1, id => { setSelectedL1(id); setSelectedL2(null); setSelectedL3(null); }, null)}
                {selectedL1 && renderPane(2, 'LEVEL 2 (CATEGORY)', l2Items, selectedL2, id => { setSelectedL2(id); setSelectedL3(null); }, selectedL1)}
                {selectedL2 && renderPane(3, 'LEVEL 3 (SUB CATEGORY)', l3Items, selectedL3, id => { setSelectedL3(id); }, selectedL2)}
                {selectedL3 && renderPane(4, 'LEVEL 4 (ITEM)', l4Items, null, null, selectedL3)}
              </div>
            </div>

            <div className="flex gap-4 p-6 border-t border-slate-800 bg-slate-800/20">
              <button 
                type="button" 
                onClick={() => isEmbedded && onCancel ? onCancel() : setView('list')} 
                className="w-1/3 bg-slate-800 hover:bg-slate-700 text-white p-3 rounded-xl font-bold transition"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={saving}
                className="w-2/3 bg-emerald-500 hover:bg-emerald-400 text-black font-bold p-3 rounded-xl transition disabled:opacity-50 shadow-lg shadow-emerald-500/20 flex justify-center items-center gap-2"
              >
                {saving ? (
                  <><svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> Saving...</>
                ) : 'Save Template'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Delete Confirmation Modal ─────────────────────────────────────────── */}
      {deleteModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0f172a] border border-rose-500/30 rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Delete Template</h3>
              <p className="text-slate-400 text-sm mb-6">
                Are you sure you want to delete this template? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteModal(null)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold py-2.5 rounded-xl transition">Cancel</button>
              <button onClick={confirmDelete} className="flex-1 bg-rose-600 hover:bg-rose-500 text-white font-semibold py-2.5 rounded-xl transition shadow-[0_0_15px_rgba(225,29,72,0.4)]">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Show Template Details Modal ─────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-[#0f172a] border border-slate-700 rounded-3xl w-full max-w-4xl shadow-2xl p-6 my-8">
            <div className="flex justify-between items-center pb-4 border-b border-slate-800">
              <div>
                <h3 className="text-xl font-bold text-white">{showModal.template.name}</h3>
                <p className="text-slate-400 text-sm mt-1">{showModal.template.description || 'No description provided'}</p>
              </div>
              <button onClick={() => setShowModal(null)} className="text-slate-400 hover:text-white p-1 hover:bg-slate-800 rounded-lg transition">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            
            <div className="py-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
              <h4 className="text-emerald-400 font-semibold mb-4">Template Structure</h4>
              {showModal.sections.length === 0 ? (
                <div className="text-slate-500 text-sm italic text-center py-8">No sections defined.</div>
              ) : (
                <div className="space-y-6">
                  {(() => {
                    const grouped = {};
                    showModal.sections.forEach(sec => {
                      const mainName = dbCategories.find(c => c.id === Number(sec.main_section_id))?.name || 'Uncategorized';
                      const catName = dbCategories.find(c => c.id === Number(sec.category_id))?.name || 'Uncategorized';
                      const subCatName = dbCategories.find(c => c.id === Number(sec.sub_category_id))?.name || 'Uncategorized';
                      const itemNames = (sec.items || [])
                        .map(itemId => dbCategories.find(c => c.id === Number(itemId))?.name)
                        .filter(Boolean);

                      if (!grouped[mainName]) grouped[mainName] = {};
                      if (!grouped[mainName][catName]) grouped[mainName][catName] = {};
                      grouped[mainName][catName][subCatName] = itemNames;
                    });

                    return Object.entries(grouped).map(([mainName, categories]) => (
                      <div key={mainName} className="bg-slate-800/30 rounded-2xl border border-slate-700/50 overflow-hidden shadow-sm">
                        <div className="bg-slate-800/80 px-5 py-3.5 border-b border-slate-700 flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                          </div>
                          <h4 className="text-emerald-400 font-bold uppercase tracking-wider text-sm">{mainName}</h4>
                        </div>
                        <div className="p-5 space-y-6">
                          {Object.entries(categories).map(([catName, subCategories]) => (
                            <div key={catName} className="pl-5 border-l-2 border-slate-700/50 space-y-4">
                              <h5 className="text-amber-300 font-bold text-sm flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-400"></div>
                                {catName}
                              </h5>
                              
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {Object.entries(subCategories).map(([subCatName, itemNames]) => (
                                  <div key={subCatName} className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50 hover:border-slate-600 transition-colors">
                                    <h6 className="text-fuchsia-300 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                                      {subCatName}
                                    </h6>
                                    <div className="flex flex-wrap gap-2 pl-2">
                                      {itemNames.length > 0 ? itemNames.map((name, i) => (
                                        <span key={i} className="bg-slate-800 border border-slate-600 text-slate-300 text-[11px] px-2.5 py-1 rounded-lg font-medium shadow-sm">
                                          {name}
                                        </span>
                                      )) : (
                                        <span className="text-slate-500 italic text-[11px]">No items selected</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* QUICK ADD ITEM MODAL */}
      {newItemModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-emerald-500/50 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-emerald-500/10 border-b border-emerald-500/20 px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">New Item</h3>
                  <p className="text-xs text-slate-400">Level 4</p>
                </div>
              </div>
              <button onClick={() => setNewItemModal({ isOpen: false, name: '', parentId: null })} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700 transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Name *</label>
                <input 
                  type="text" 
                  value={newItemModal.name} 
                  onChange={e => setNewItemModal(p => ({ ...p, name: e.target.value }))} 
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newItemModal.name.trim()) {
                      e.preventDefault();
                      handleQuickCreateItem(newItemModal.name, newItemModal.parentId);
                      setNewItemModal({ isOpen: false, name: '', parentId: null });
                    }
                  }}
                  autoFocus 
                  className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition placeholder-slate-600" 
                  placeholder="Enter name" 
                />
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-800">
                <button type="button" onClick={() => setNewItemModal({ isOpen: false, name: '', parentId: null })} className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-3.5 rounded-xl transition text-sm">Cancel</button>
                <button 
                  type="button" 
                  disabled={!newItemModal.name.trim()} 
                  onClick={() => {
                    handleQuickCreateItem(newItemModal.name, newItemModal.parentId);
                    setNewItemModal({ isOpen: false, name: '', parentId: null });
                  }} 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3.5 rounded-xl transition disabled:opacity-50 text-sm flex justify-center items-center gap-2"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (isEmbedded) return innerView;

  return (
    <Layout title="Report Templates" subtitle="Manage all report templates">
      <Toaster position="top-right" />
      {innerView}
    </Layout>
  );
};

export default TemplatesList;
